import React from 'react';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import { asAxisCurrency } from '../../shared/utils/dashboard-page.utils';
import { BarRow, SectionKey } from '../../shared/types/dashboard-page.types';
import classes from './sales-graph-section.module.scss';

export type SalesGraphSectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  graphRows: BarRow[];
};

const AXIS_TICK_COUNT = 7;

// Rounds the axis max up to a "nice" step (1/2/5 * 10^n) so tick labels read cleanly.
const niceAxisMax = (maxValue: number): number => {
  if (maxValue <= AXIS_TICK_COUNT - 1) {
    return AXIS_TICK_COUNT - 1;
  }
  const rawStep = maxValue / (AXIS_TICK_COUNT - 1);
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const step = niceNormalized * magnitude;
  return step * (AXIS_TICK_COUNT - 1);
};

export const SalesGraphSection: React.FC<SalesGraphSectionProps> = ({
  collapsedSections,
  toggleSection,
  graphRows,
}) => {
  const maxValue = Math.max(1, ...graphRows.map((row) => row.value));
  const axisMax = niceAxisMax(maxValue);
  const axisTicks = Array.from({ length: AXIS_TICK_COUNT }, (_, index) => (axisMax / (AXIS_TICK_COUNT - 1)) * index);

  return (
    <SectionCard title="גרף מכירות" sectionKey="salesGraph" collapsed={collapsedSections.salesGraph} onToggle={() => toggleSection('salesGraph')}>
      <div className={classes.chart}>
        <div className={classes.plotArea}>
          <div className={classes.plotScale}>
            <div className={classes.gridlines}>
              {axisTicks.map((tick, index) => (
                <span
                  key={tick}
                  className={classes.gridline}
                  style={{ left: `${(index / (AXIS_TICK_COUNT - 1)) * 100}%` }}
                />
              ))}
            </div>
          </div>
          <div className={classes.bars}>
            {graphRows.map((row) => (
              <div key={row.label} className={classes.barRow}>
                <span className={classes.barLabel}>{row.label}</span>
                <div className={classes.barTrack}>
                  <div className={classes.bar} style={{ width: `${Math.max(2, Math.round((row.value / axisMax) * 100))}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={classes.axisRow}>
          <span className={classes.axisSpacer} />
          <div className={classes.axisLabels}>
            {axisTicks.map((tick, index) => (
              <span
                key={tick}
                className={classes.axisLabel}
                style={{ left: `${(index / (AXIS_TICK_COUNT - 1)) * 100}%` }}
              >
                {asAxisCurrency(tick)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </SectionCard>
  );
};
